import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { getAppointments } from "@/api/services";
import Header from "@/components/Header";
import { MessageSquare, Clock, Calendar, Loader2, Search } from "lucide-react";
import { cn } from "@/utils/cn";
import { format } from "date-fns";

const getMaskedId = (id) => {
  if (!id) return "Client";
  const shortId = id.toString().slice(-6).toUpperCase();
  return `#${shortId}`;
};

const getInitials = (name) => {
  if (!name) return "?";
  return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
};

export default function ChatsPage() {
  const { astrologer } = useAuth();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!astrologer?.astrologerId || !astrologer?.accessToken) return;

    setLoading(true);
    getAppointments(astrologer.astrologerId, astrologer.accessToken)
      .then(({ data }) => {
        if (data?.appointments) {
          // Filter only chat appointments
          const chatAppointments = data.appointments.filter(
            apt => apt.mode === "chat"
          );
          setChats(chatAppointments);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [astrologer]);

  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "bg-green-100 text-green-700";
      case "pending": return "bg-yellow-100 text-yellow-700";
      case "cancelled": 
      case "expired": return "bg-red-100 text-red-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const filteredChats = chats
    .filter(chat => 
      !searchTerm || 
      chat.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getMaskedId(chat.user?._id).toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <>
      <Header name="Chat History" />
      <div className="w-full p-4">
        {/* Search */}
        <div className="bg-white rounded-lg shadow-sm mb-4">
          <div className="p-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
        </div>

        {/* Chat List */}
        <div className="bg-white rounded-lg shadow-sm">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-6 w-6 text-orange-500 animate-spin" />
            </div>
          ) : filteredChats.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No chat history found</p>
              <p className="text-xs text-gray-400 mt-1">Chat consultations will appear here</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredChats.map((chat) => (
                <div key={chat._id} className="flex items-center gap-4 p-4 hover:bg-gray-50">
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center flex-shrink-0">
                    {chat.user?.picture ? (
                      <img src={chat.user.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <span className="text-sm font-medium">{getInitials(chat.user?.name)}</span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900">{chat.user?.name || "Unknown"}</p>
                      <span className="text-xs text-gray-400">{getMaskedId(chat.user?._id)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Calendar className="h-3 w-3" />
                      <span>{format(new Date(chat.date), "MMM d, yyyy")}</span>
                      <span>·</span>
                      <span>{formatTime(chat.time)}</span>
                      {chat.duration > 0 && (
                        <>
                          <span>·</span>
                          <Clock className="h-3 w-3" />
                          <span>{chat.duration}m</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Status */}
                  <span className={cn("text-xs px-2 py-1 rounded-full", getStatusColor(chat.status))}>
                    {chat.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
